import { useId } from 'react';
import { cn } from './cn.js';
import { KANBAN_COLORS, type KanbanColor } from './kanbanState.js';

/**
 * As classes de cada cor, escritas por extenso.
 *
 * O Tailwind só emite a classe que ele LÊ no fonte: `bg-fase-${cor}-suave`
 * montado em tempo de execução desenharia transparente, sem erro nenhum. Por
 * isso a tabela, e por isso o `Record` — cor nova em `KANBAN_COLORS` não
 * compila até ganhar a linha dela aqui.
 *
 * Mora fora do `Kanban` desde que a etiqueta (07/10/2026) passou a usar a
 * mesma paleta: a fase e a etiqueta são as duas coisas da casa com cor
 * escolhida por quem usa, e as duas escolhem da mesma lista medida.
 */
export const COLOR_CLASSES: Record<KanbanColor, { column: string; dot: string }> = {
  // chaves = `KanbanColor`, o valor gravado na fase — valor, não identificador
  // @valor
  cinza: { column: 'bg-fase-cinza-suave', dot: 'bg-fase-cinza' },
  azul: { column: 'bg-fase-azul-suave', dot: 'bg-fase-azul' },
  ciano: { column: 'bg-fase-ciano-suave', dot: 'bg-fase-ciano' },
  turquesa: { column: 'bg-fase-turquesa-suave', dot: 'bg-fase-turquesa' },
  verde: { column: 'bg-fase-verde-suave', dot: 'bg-fase-verde' },
  amarelo: { column: 'bg-fase-amarelo-suave', dot: 'bg-fase-amarelo' },
  laranja: { column: 'bg-fase-laranja-suave', dot: 'bg-fase-laranja' },
  vermelho: { column: 'bg-fase-vermelho-suave', dot: 'bg-fase-vermelho' },
  rosa: { column: 'bg-fase-rosa-suave', dot: 'bg-fase-rosa' },
  roxo: { column: 'bg-fase-roxo-suave', dot: 'bg-fase-roxo' },
};

/**
 * A escolha de uma cor da paleta de fase — um grupo de rádios com a amostra
 * de cada cor. Hex livre não entra: cor escolhida a olho passa no claro e
 * some no escuro, e esta lista já passou pela régua de contraste.
 */
export function ColorPicker({
  label = 'Cor',
  value,
  onChange,
  disabled = false,
  error,
}: {
  label?: string;
  value: KanbanColor;
  onChange: (color: KanbanColor) => void;
  disabled?: boolean;
  error?: string | null;
}) {
  const group = useId();
  const errorId = `${group}-erro`;
  return (
    <fieldset disabled={disabled}>
      <legend className="text-sm font-medium">{label}</legend>
      <div className="mt-2 flex flex-wrap gap-1.5">
        {KANBAN_COLORS.map((c) => {
          const classes = COLOR_CLASSES[c.value];
          return (
            <label
              key={c.value}
              title={c.label}
              className={cn(disabled ? 'cursor-not-allowed' : 'cursor-pointer')}
            >
              <input
                type="radio"
                name={group}
                value={c.value}
                checked={value === c.value}
                onChange={() => onChange(c.value)}
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                className="peer sr-only"
              />
              <span
                className={cn(
                  'grid size-9 place-items-center rounded-pilula border-2 border-transparent transition-colors pointer-coarse:size-11',
                  'hover:border-borda peer-checked:border-texto peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-foco',
                  'peer-disabled:opacity-50 peer-disabled:hover:border-transparent',
                  error && 'peer-checked:border-erro',
                  classes.column,
                )}
              >
                <span className={cn('size-3.5 rounded-pilula', classes.dot)} />
              </span>
              <span className="sr-only">{c.label}</span>
            </label>
          );
        })}
      </div>
      {error && (
        <p id={errorId} className="mt-1.5 text-xs text-erro">
          {error}
        </p>
      )}
    </fieldset>
  );
}
